// Apolo IDE — CheckpointBar (S4 · E5-WS1-T01).
//
// Lists the GREEN checkpoints saved this run (state restorable after each
// honest-green) with a "Restaurar" action. Restore rewinds the WHOLE workspace,
// so it asks for an inline confirmation first (reversibility heuristic).
// Presentational + props-driven; S1 --rx-* tokens, like TaskNode.

import { useState } from 'react';
import { History, RotateCcw, CheckCircle } from 'lucide-react';

const shortTask = (id) => (id && id.length > 10 ? `${id.slice(0, 8)}…` : (id ?? ''));

function fmtTime(ms) {
  if (!Number.isFinite(ms)) return '';
  try {
    return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const ghostBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11,
  color: 'var(--rx-tx2)', cursor: 'pointer', background: 'var(--rx-s3)',
  border: '1px solid var(--rx-border)', borderRadius: 8, padding: '5px 9px', flexShrink: 0,
};

/**
 * @param {{ checkpoints?: Array<{id:string, taskId:string, createdAt:number}>, onRestore?: (cp:object) => void }} props
 */
export function CheckpointBar({ checkpoints = [], onRestore }) {
  const list = Array.isArray(checkpoints) ? checkpoints : [];
  const [pendingId, setPendingId] = useState(null);

  if (list.length === 0) {
    return (
      <div
        data-testid="checkpoint-bar-empty"
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 12px', fontSize: 11, color: 'var(--rx-tx2)',
          border: '1px dashed var(--rx-border)', borderRadius: 10, background: 'var(--rx-s1)',
        }}
      >
        <History size={14} aria-hidden="true" style={{ color: 'var(--rx-tx4)', flexShrink: 0 }} />
        <span>Nenhum checkpoint ainda — um é salvo após cada honest-green.</span>
      </div>
    );
  }

  return (
    <div
      data-testid="checkpoint-bar"
      role="list"
      aria-label="Checkpoints — estado verde restaurável"
      style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      {list.map((cp, i) => {
        const confirming = pendingId === (cp.id || i);
        return (
          <div
            key={cp.id || i}
            role="listitem"
            data-testid="checkpoint-item"
            data-checkpoint-id={cp.id}
            style={{
              display: 'flex', alignItems: 'center', gap: 9,
              padding: '8px 12px', background: 'var(--rx-success-soft)',
              border: '1px solid rgba(91,138,94,.3)', borderRadius: 'var(--rx-r-md)',
            }}
          >
            <CheckCircle size={14} aria-hidden="true" style={{ color: 'var(--rx-add)', flexShrink: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
              <span style={{ fontSize: 11, color: 'var(--rx-tx1)', fontFamily: 'var(--rx-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {shortTask(cp.taskId)}
              </span>
              <span style={{ fontSize: 10, color: 'var(--rx-tx3)' }}>{fmtTime(cp.createdAt)}</span>
            </div>

            {confirming ? (
              <div data-testid="checkpoint-confirm" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 10, color: 'var(--rx-tx2)' }}>Apaga o estado atual.</span>
                <button
                  type="button"
                  data-testid="checkpoint-confirm-yes"
                  onClick={() => { setPendingId(null); onRestore?.(cp); }}
                  aria-label={`Confirmar restauração de ${shortTask(cp.taskId)}`}
                  style={{ ...ghostBtn, color: 'var(--rx-del)' }}
                >
                  Sim, restaurar
                </button>
                <button
                  type="button"
                  data-testid="checkpoint-confirm-no"
                  onClick={() => setPendingId(null)}
                  aria-label="Cancelar restauração"
                  style={ghostBtn}
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="checkpoint-restore"
                className="rx-checkpoint-restore"
                onClick={() => setPendingId(cp.id || i)}
                aria-label={`Restaurar checkpoint de ${shortTask(cp.taskId)}`}
                style={ghostBtn}
              >
                <RotateCcw size={12} aria-hidden="true" />
                Restaurar
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default CheckpointBar;
